import React, { JSX, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { CommandDdbItem } from '../../../../types/Commands';
import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import { ModeBadge, battColourClass, formatBatteryLabel, formatLastHeardLabel, pickMode } from '../../DeviceListView/panels/deviceLiveUi';
import { INPUT, ONLINE_TIMEOUT_MS } from '../../DeviceListView/utils/deviceListConstants';
import { isCommandActive } from '../../DeviceListView/utils/deviceListHelpers';
import { isRearRaceModeActive, pairedFrontIdFromRear } from '../../../helpers/devicePairing';
import { useDeviceListLiveData } from '../../DeviceListView/utils/useDeviceListLiveData';
import DeviceIdTableFilterBar, {
  deviceIdMatchesTableFilter,
} from '../../shared/DeviceIdTableFilterBar';
import { PortaledLayer } from '../../shared/PortaledLayer';
import { DeviceRosterOverlay } from '../../shared/DeviceRosterOverlay';
import {
  TABLE_BODY_CLASS,
  TABLE_CLASS,
  TABLE_HEAD_CELL_CLASS,
  TABLE_HEAD_STICKY_CLASS,
  resolveAssignedDeviceRowHighlight,
} from '../../shared/tableDesign';
import { EventDeviceRow } from '../types';
import { formatTime, formatTs, formatUserIdentityDisplay, toMillis } from '../../../helpers/helpers';
import { type RaceConfigurationRequirements } from '../utils';
import { raceStatusShowsRetrieval } from '../utils/raceRetrievalCounts';
import {
  getDeviceValidationMismatchReasons,
  isDeviceSubjectToFleetValidation,
  isDeviceValidationStaleReason,
  isRaceConfigurationReadyForDeviceVerification,
  toDeviceStatusSeenMs,
} from '../../../helpers/deviceValidation';

type AssignedDevicesColumnKey =
  | 'mode'
  | 'firmware'
  | 'debugLog'
  | 'raceLog'
  | 'battery'
  | 'lastOnline'
  | 'locationUpdated'
  | 'assignedAthleteName'
  | 'assignedAthleteRaceNo'
  | 'racked'
  | 'assignedBy'
  | 'assignedAt';
type AssignedDevicesSortKey = 'validation' | 'deviceId' | AssignedDevicesColumnKey;
type AssignedDevicesSortDirection = 'asc' | 'desc';

const ASSIGNED_DEVICES_COLUMNS: Array<{ key: AssignedDevicesColumnKey; label: string }> = [
  { key: 'mode', label: 'Mode' },
  { key: 'firmware', label: 'Firmware' },
  { key: 'battery', label: 'Battery (V)' },
  { key: 'lastOnline', label: 'Last online' },
  { key: 'assignedAthleteName', label: 'Athlete name' },
  { key: 'assignedAthleteRaceNo', label: 'Athlete #' },
  { key: 'racked', label: 'Racked' },
  { key: 'debugLog', label: 'Debug log (B)' },
  { key: 'raceLog', label: 'Race log (B)' },
  { key: 'locationUpdated', label: 'Location updated' },
  { key: 'assignedBy', label: 'Assigned by' },
  { key: 'assignedAt', label: 'Assigned at' },
];
const COLUMNS_MENU_MIN_WIDTH = 260;
const COLUMNS_MENU_MAX_WIDTH = 340;

type Props = {
  eventId: string;
  devices: EventDeviceRow[];
  /** When set, retrieved column and filters hide until Distribution. */
  raceStatus?: string;
  statusReloadKey?: string | number;
  isAdmin: boolean;
  isReadOnly: boolean;
  lockedHint: string;
  globalUserIdToDisplayName: Record<string, string>;
  selectedDeviceIds: string[];
  setSelectedDeviceIds: (deviceIds: string[]) => void;
  onToggleDeviceSelected: (deviceId: string) => void;
  onRemoveDevice?: (eventId: string, deviceId: string) => void;
  /** When set, row checkboxes support bulk remove from the event (footer in parent). */
  onRemoveDevicesBulk?: (eventId: string, deviceIds: string[]) => void;
  onRefresh?: () => void | Promise<void>;
  retrievedPhaseByDeviceId?: Record<string, string>;
};

type DeviceDisplayRow = {
  primaryDeviceId: string;
  rearDeviceId: string;
  frontDeviceId: string;
  device: EventDeviceRow;
  rearDevice?: EventDeviceRow;
  frontDevice?: EventDeviceRow;
  mismatchReasons: string[];
  lifecycleRow: Record<string, unknown> | undefined;
};

function toLocationUpdatedMs(status?: DeviceStatus): number {
  const ts = status?.last_location_updated_at;
  if (typeof ts !== 'number' || !Number.isFinite(ts) || ts <= 0) return 0;
  return toMillis(ts);
}

function isRearDeviceId(deviceId: string): boolean {
  return /^rear_/i.test(deviceId);
}

function isFrontDeviceId(deviceId: string): boolean {
  return /^front_/i.test(deviceId);
}

function isUnusedDevice(device: EventDeviceRow): boolean {
  return String(device.assignmentStatus ?? '').trim().toUpperCase() === 'UNUSED';
}

function renderUnusedBadge(compact = false): JSX.Element {
  return (
    <span
      className={[
        'inline-flex items-center rounded border border-slate-300 bg-slate-100 font-semibold uppercase tracking-wide text-slate-600',
        'dark:border-slate-600 dark:bg-slate-800/70 dark:text-slate-300',
        compact ? 'px-1 py-0 text-[9px]' : 'px-1.5 py-0.5 text-[10px]',
      ].join(' ')}
      title="Unused replacement device. Athlete assignment has been removed."
    >
      Unused
    </span>
  );
}

const ASSIGNED_DEVICE_ROW_STATE_BASE = 'border-y-0 border-r-0 border-t-0 border-solid border-l-4';
const ASSIGNED_DEVICE_UNUSED_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_STATE_BASE} !border-l-slate-400 bg-slate-100/80 text-slate-500`
  + ' dark:!border-l-slate-600 dark:bg-slate-950/50 dark:text-slate-400 opacity-75';
const ASSIGNED_DEVICE_RETRIEVED_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_STATE_BASE} !border-l-amber-500 bg-amber-100/70`
  + ' dark:!border-l-amber-400 dark:bg-amber-950/30';
const ASSIGNED_DEVICE_FRONT_ONLY_ROW_CLASS =
  `${ASSIGNED_DEVICE_ROW_STATE_BASE} !border-l-rose-500 bg-rose-50`
  + ' dark:!border-l-rose-400 dark:bg-rose-950/20';

export default function EventAssignedDevicesTable(props: Props): JSX.Element {
  const {
    eventId,
    devices,
    raceStatus,
    statusReloadKey,
    isAdmin,
    isReadOnly,
    lockedHint,
    globalUserIdToDisplayName,
    selectedDeviceIds,
    setSelectedDeviceIds,
    onToggleDeviceSelected,
    onRemoveDevice,
    onRemoveDevicesBulk,
    onRefresh,
    retrievedPhaseByDeviceId = {},
  } = props;

  const showDeviceRemovalSelection = isAdmin && !!onRemoveDevicesBulk;
  const showRetrievalUi = raceStatus == null || raceStatusShowsRetrieval(raceStatus);

  function isColumnVisible(key: AssignedDevicesColumnKey): boolean {
    return visibleColumns.includes(key);
  }

  const deviceIds = useMemo(
    () => devices.map((d) => String(d.deviceId || '').trim()).filter(Boolean),
    [devices],
  );

  const activeRequestIdRef = useRef<string | null>(null);
  const [, setRequestLatestCommandMap] = useState<Record<string, CommandDdbItem>>({});
  const [deviceIdFilter, setDeviceIdFilter] = useState('');
  const [rosterDeviceId, setRosterDeviceId] = useState<string | null>(null);
  const [devicesViewExpanded, setDevicesViewExpanded] = useState(false);
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<AssignedDevicesColumnKey[]>([
    'mode',
    'firmware',
    'battery',
    'lastOnline',
    'assignedAthleteName',
    'assignedAthleteRaceNo',
    'racked',
  ]);
  const columnsMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [columnsMenuCoords, setColumnsMenuCoords] = useState({
    top: 0,
    left: 0,
    width: COLUMNS_MENU_MIN_WIDTH,
    maxHeight: 320,
  });
  const [sortKey, setSortKey] = useState<AssignedDevicesSortKey>('validation');
  const [sortDirection, setSortDirection] = useState<AssignedDevicesSortDirection>('asc');
  const [hideRetrievedRows, setHideRetrievedRows] = useState(false);
  const [refreshingRows, setRefreshingRows] = useState(false);

  useEffect(() => {
    if (!devicesViewExpanded) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setDevicesViewExpanded(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [devicesViewExpanded]);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') setColumnsMenuOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [columnsMenuOpen]);

  useLayoutEffect(() => {
    if (!columnsMenuOpen) return;

    function updatePosition(): void {
      const btn = columnsMenuButtonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const width = Math.min(
        COLUMNS_MENU_MAX_WIDTH,
        Math.max(COLUMNS_MENU_MIN_WIDTH, Math.min(r.width + 140, window.innerWidth - 24)),
      );
      let left = r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      const desiredHeight = 520;
      const availableBelow = window.innerHeight - r.bottom - 12;
      const availableAbove = r.top - 12;
      const preferAbove = availableBelow < 220 && availableAbove > availableBelow;
      const maxHeight = Math.max(180, Math.min(desiredHeight, preferAbove ? availableAbove : availableBelow));
      const top = preferAbove ? Math.max(8, r.top - maxHeight - 6) : r.bottom + 6;
      setColumnsMenuCoords({ top, left, width, maxHeight });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [columnsMenuOpen]);
  const [raceConfigurationByRaceId, setRaceConfigurationByRaceId] = useState<
    Record<string, RaceConfigurationRequirements | null>
  >({});
  const [raceConfigurationLoading, setRaceConfigurationLoading] = useState(false);
  const [lifecycleById, setLifecycleById] = useState<Record<string, Record<string, unknown>>>({});

  const { statusMap, lastStatusSeen, now, latestCommandMap, commandStatusMap } = useDeviceListLiveData({
    activeRequestIdRef,
    setRequestLatestCommandMap,
    deviceIds,
    operatorScopedEventId: eventId,
    enableInitialStatusQuery: true,
    enableInitialCommandQuery: false,
    statusReloadKey,
  });

  const uniqueRaceIds = useMemo(() => {
    const s = new Set<string>();
    for (const d of devices) {
      const r = String(d.raceId ?? '').trim();
      if (r) s.add(r);
    }
    return Array.from(s);
  }, [devices]);

  useEffect(() => {
    const api = (window.eventsAPI as { getRaceConfiguration?: (p: { eventId: string; raceId: string }) => Promise<unknown> } | undefined)
      ?.getRaceConfiguration;
    if (!api || uniqueRaceIds.length === 0) {
      setRaceConfigurationByRaceId({});
      setRaceConfigurationLoading(false);
      return;
    }
    let cancelled = false;
    setRaceConfigurationLoading(true);
    void Promise.all(
      uniqueRaceIds.map((raceId) =>
        api({ eventId, raceId }).then((res: unknown) => {
          if (cancelled) return { raceId, cfg: null as RaceConfigurationRequirements | null };
          const r = res as { ok?: boolean; data?: Record<string, unknown> } | null | undefined;
          if (!r?.ok) return { raceId, cfg: null };
          const data = (r?.data ?? {}) as Record<string, unknown>;
          const raw = (data.configuration ?? {}) as Record<string, unknown>;
          const cfg: RaceConfigurationRequirements = {
            fw_version: typeof raw.fw_version === 'string' ? raw.fw_version.trim() : '',
            fw_version_front: typeof raw.fw_version_front === 'string' ? raw.fw_version_front.trim() : '',
            min_battery: Number.isFinite(Number(raw.min_battery)) ? Number(raw.min_battery) : undefined,
            max_debug_size: Number.isFinite(Number(raw.max_debug_size)) ? Number(raw.max_debug_size) : undefined,
            max_race_size: Number.isFinite(Number(raw.max_race_size)) ? Number(raw.max_race_size) : undefined,
          };
          return { raceId, cfg };
        }),
      ),
    )
      .then((rows) => {
        if (cancelled) return;
        const m: Record<string, RaceConfigurationRequirements | null> = {};
        for (const { raceId, cfg } of rows) m[raceId] = cfg;
        setRaceConfigurationByRaceId(m);
      })
      .catch(() => {
        if (!cancelled) setRaceConfigurationByRaceId({});
      })
      .finally(() => {
        if (!cancelled) setRaceConfigurationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, uniqueRaceIds, statusReloadKey]);

  useEffect(() => {
    const api = (window.eventsAPI as { getDeviceLifecycle?: (args: { deviceIds: string[] }) => Promise<unknown> } | undefined)
      ?.getDeviceLifecycle;
    if (!api || deviceIds.length === 0) {
      setLifecycleById({});
      return;
    }
    let cancelled = false;
    void api({ deviceIds })
      .then((res: unknown) => {
        if (cancelled) return;
        const r = res as { ok?: boolean; data?: { devices?: unknown[] } } | null | undefined;
        if (!r?.ok) {
          setLifecycleById({});
          return;
        }
        const rows = Array.isArray(r.data?.devices) ? r.data.devices : [];
        const map: Record<string, Record<string, unknown>> = {};
        for (const raw of rows) {
          if (!raw || typeof raw !== 'object') continue;
          const row = raw as Record<string, unknown>;
          const deviceId = String(row.deviceId ?? '').trim();
          if (deviceId) map[deviceId] = row;
        }
        setLifecycleById(map);
      })
      .catch(() => {
        if (!cancelled) setLifecycleById({});
      });
    return () => {
      cancelled = true;
    };
  }, [deviceIds, statusReloadKey]);

  const devicesOrderedFiltered = useMemo(() => {
    const byAthlete = new Map<string, { rear?: EventDeviceRow; front?: EventDeviceRow; other: EventDeviceRow[] }>();
    const standalone: EventDeviceRow[] = [];
    for (const device of devices) {
      const did = String(device.deviceId || '').trim();
      const athleteId = String(device.assignedAthleteId || '').trim();
      if (!did || !athleteId) {
        standalone.push(device);
        continue;
      }
      const entry = byAthlete.get(athleteId) ?? { other: [] };
      if (isRearDeviceId(did)) entry.rear = device;
      else if (isFrontDeviceId(did)) entry.front = device;
      else entry.other.push(device);
      byAthlete.set(athleteId, entry);
    }

    const groupedRows: DeviceDisplayRow[] = [];
    const pushFromDevice = (device: EventDeviceRow): void => {
      const id = String(device.deviceId || '').trim();
      if (!id) return;
      const status = statusMap[id];
      const lifecycleRow = lifecycleById[id];
      const rid = String(device.raceId ?? '').trim();
      const raceCfg = rid ? raceConfigurationByRaceId[rid] ?? null : null;
      const pairedFrontId = isRearDeviceId(id) ? pairedFrontIdFromRear(id) : null;
      const frontStatus = pairedFrontId ? statusMap[pairedFrontId] : undefined;
      groupedRows.push({
        primaryDeviceId: id,
        rearDeviceId: isRearDeviceId(id) ? id : '',
        frontDeviceId: '',
        device,
        rearDevice: isRearDeviceId(id) ? device : undefined,
        frontDevice: undefined,
        mismatchReasons: isDeviceSubjectToFleetValidation(id)
          ? getDeviceValidationMismatchReasons(raceCfg, status, lifecycleRow, now, frontStatus)
          : [],
        lifecycleRow,
      });
    };

    for (const [athleteId, pair] of byAthlete.entries()) {
      const rear = pair.rear;
      if (!rear) {
        if (pair.front) pushFromDevice(pair.front);
        pair.other.forEach(pushFromDevice);
        continue;
      }
      const frontCandidate = pair.front;
      const extraOthers = pair.other;
      const primary = rear;
      const primaryId = String(primary.deviceId || '').trim();
      if (!primaryId) continue;
      const validationDeviceId = String(rear?.deviceId ?? '').trim();
      const status = validationDeviceId ? statusMap[validationDeviceId] : undefined;
      const lifecycleRow = validationDeviceId ? lifecycleById[validationDeviceId] : undefined;
      const rid = String(primary.raceId ?? rear?.raceId ?? frontCandidate?.raceId ?? '').trim();
      const raceCfg = rid ? raceConfigurationByRaceId[rid] ?? null : null;
      const frontDeviceIdForValidation = String(frontCandidate?.deviceId ?? '').trim();
      const frontStatusForValidation = frontDeviceIdForValidation
        ? statusMap[frontDeviceIdForValidation]
        : undefined;
      const mergedAthleteName = String(
        rear?.assignedAthleteName
        ?? frontCandidate?.assignedAthleteName
        ?? primary.assignedAthleteName
        ?? '',
      ).trim();
      const mergedAthleteRaceNo = String(
        rear?.assignedAthleteRaceNo
        ?? frontCandidate?.assignedAthleteRaceNo
        ?? primary.assignedAthleteRaceNo
        ?? '',
      ).trim();
      const mergedRow: EventDeviceRow = {
        ...primary,
        deviceId: primaryId,
        assignedAthleteId: athleteId || undefined,
        assignedAthleteName: mergedAthleteName || undefined,
        assignedAthleteRaceNo: mergedAthleteRaceNo || undefined,
        assignedAt: rear?.assignedAt ?? frontCandidate?.assignedAt ?? primary.assignedAt,
        assignedBy: rear?.assignedBy ?? frontCandidate?.assignedBy ?? primary.assignedBy,
        recalled: Number(primary.recalled ?? 0),
        retrieved: Number(primary.retrieved ?? 0),
        racked: Number(primary.racked ?? 0),
      };
      groupedRows.push({
        primaryDeviceId: primaryId,
        rearDeviceId: String(rear?.deviceId || '').trim(),
        frontDeviceId: String(frontCandidate?.deviceId || '').trim(),
        device: mergedRow,
        rearDevice: rear,
        frontDevice: frontCandidate,
        mismatchReasons: validationDeviceId
          ? getDeviceValidationMismatchReasons(raceCfg, status, lifecycleRow, now, frontStatusForValidation)
          : [],
        lifecycleRow,
      });
      for (const leftover of extraOthers) {
        pushFromDevice(leftover);
      }
    }
    for (const device of standalone) pushFromDevice(device);

    const filtered = groupedRows.filter((row) =>
      deviceIdMatchesTableFilter(row.primaryDeviceId, deviceIdFilter)
      || (row.frontDeviceId ? deviceIdMatchesTableFilter(row.frontDeviceId, deviceIdFilter) : false),
    );

    function toComparableValue(row: DeviceDisplayRow, key: AssignedDevicesSortKey): number | string {
      const deviceId = String(row.primaryDeviceId || '').trim();
      const status = statusMap[deviceId];
      const lastSeen = Math.max(lastStatusSeen[deviceId] ?? 0, toDeviceStatusSeenMs(status));
      const cmdStatus = commandStatusMap[deviceId] ?? latestCommandMap[deviceId]?.status;
      const online = lastSeen >= now - ONLINE_TIMEOUT_MS || isCommandActive(cmdStatus);
      const mode = pickMode(online, status);
      const rackedValue = Number(row.device.racked ?? 0) > 0 ? 1 : 0;

      if (key === 'validation') return row.mismatchReasons.length > 0 ? 1 : 0;
      if (key === 'deviceId') return row.rearDeviceId || row.primaryDeviceId;
      if (key === 'mode') return mode || '';
      if (key === 'firmware') return String(status?.fw || '');
      if (key === 'debugLog') return typeof status?.debug_log_size === 'number' ? status.debug_log_size : -1;
      if (key === 'raceLog') return typeof status?.race_log_size === 'number' ? status.race_log_size : -1;
      if (key === 'battery') return typeof status?.batt_mv === 'number' ? status.batt_mv : -1;
      if (key === 'lastOnline') return lastSeen;
      if (key === 'locationUpdated') return toLocationUpdatedMs(status);
      if (key === 'assignedAthleteName') {
        return String(row.device.assignedAthleteName || '');
      }
      if (key === 'assignedAthleteRaceNo') {
        return String(row.device.assignedAthleteRaceNo || row.device.assignedAthleteId || '');
      }
      if (key === 'racked') return rackedValue;
      
      if (key === 'assignedBy') return formatUserIdentityDisplay(row.device.assignedBy, globalUserIdToDisplayName);
      if (key === 'assignedAt') return Number(row.device.assignedAt ?? 0);
      return '';
    }

    filtered.sort((a, b) => {
      const av = toComparableValue(a, sortKey);
      const bv = toComparableValue(b, sortKey);
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      if (cmp === 0) {
        cmp = String(a.primaryDeviceId || '').localeCompare(String(b.primaryDeviceId || ''));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return hideRetrievedRows
      ? filtered.filter((row) => Number(row.device.retrieved ?? row.device.recalled ?? 0) !== 1)
      : filtered;
  }, [
    commandStatusMap,
    deviceIdFilter,
    devices,
    globalUserIdToDisplayName,
    lastStatusSeen,
    latestCommandMap,
    lifecycleById,
    hideRetrievedRows,
    now,
    raceConfigurationByRaceId,
    sortDirection,
    sortKey,
    statusMap,
  ]);

  /** True when any race on these rows lacks saved configuration with a firmware version (needed for validation). */
  const needsRaceConfigurationWarning = useMemo(() => {
    if (raceConfigurationLoading) return false;
    if (uniqueRaceIds.length === 0) return false;
    for (const rid of uniqueRaceIds) {
      const cfg = raceConfigurationByRaceId[rid];
      if (!isRaceConfigurationReadyForDeviceVerification(cfg)) return true;
    }
    return false;
  }, [raceConfigurationLoading, uniqueRaceIds, raceConfigurationByRaceId]);

  const raceConfigurationNotice = needsRaceConfigurationWarning ? (
    <div className="flex items-center gap-2 border-b border-amber-200/70 bg-amber-50/90 px-3 py-1.5 dark:border-amber-800/50 dark:bg-amber-950/50">
      <span
        className="inline-flex shrink-0 rounded border border-amber-300/90 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 shadow-sm dark:border-amber-700/70 dark:bg-amber-900/60 dark:text-amber-100"
        title="Race configuration"
      >
        Warning
      </span>
      <p className="min-w-0 flex-1 text-[11px] leading-tight text-amber-950 dark:text-amber-100/95">
        Submit a race configuration (firmware) to enable automatic device–race validation.
      </p>
    </div>
  ) : null;

  const visibleDeviceIds = useMemo(
    () =>
      devicesOrderedFiltered
        .map((row) => String(row.primaryDeviceId || '').trim())
        .filter(Boolean),
    [devicesOrderedFiltered],
  );
  const tableColumnCount = useMemo(() => {
    let n = 1; // rear device column always visible
    if (showDeviceRemovalSelection) n += 1;
    n += visibleColumns.length;
    if (isAdmin && onRemoveDevice) n += 1;
    return n;
  }, [showDeviceRemovalSelection, visibleColumns.length, isAdmin, onRemoveDevice]);


  const isAllSelected =
    visibleDeviceIds.length > 0 &&
    visibleDeviceIds.every((id) => selectedDeviceIds.includes(id));

  function toggleColumn(key: AssignedDevicesColumnKey): void {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  }

  function toggleSort(nextKey: AssignedDevicesSortKey): void {
    setSortKey((prevKey) => {
      if (prevKey === nextKey) {
        setSortDirection((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortDirection('asc');
      return nextKey;
    });
  }

  function sortIndicator(key: AssignedDevicesSortKey): string {
    if (sortKey !== key) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  }

  function renderRetrievedPhaseBadge(phase: string, compact = false): JSX.Element | null {
    const normalized = String(phase || '').trim().toUpperCase();
    const label = normalized ? `Retrieved (${normalized})` : 'Retrieved';
    return (
      <span
        className={
          compact
            ? 'inline-flex max-w-full items-center rounded border border-amber-200/80 bg-amber-50/60 px-1.5 py-px text-[9px] font-medium leading-tight text-amber-800/90 dark:border-amber-800/50 dark:bg-amber-950/25 dark:text-amber-200/80'
            : 'inline-flex max-w-full items-center rounded-md border border-amber-200/90 bg-amber-50/80 px-2 py-0.5 text-[10px] font-medium leading-tight text-amber-900/90 dark:border-amber-800/55 dark:bg-amber-950/30 dark:text-amber-200/85'
        }
        title={normalized ? `Retrieved during ${normalized}` : 'Retrieved'}
      >
        <span className="truncate">{label}</span>
      </span>
    );
  }

  function renderAssignedDevicesDataTable(): JSX.Element {
    if (devicesOrderedFiltered.length === 0) {
      return (
        <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">No devices match the filter.</div>
      );
    }
    return (
      <table className={TABLE_CLASS}>
        <thead className={TABLE_HEAD_STICKY_CLASS}>
          <tr>
            {showDeviceRemovalSelection ? (
              <th className={`${TABLE_HEAD_CELL_CLASS} w-10`}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  disabled={visibleDeviceIds.length === 0 || isReadOnly}
                  onChange={(e) => {
                    if (isReadOnly) return;
                    if (e.target.checked) {
                      setSelectedDeviceIds([...new Set([...selectedDeviceIds, ...visibleDeviceIds])]);
                    } else {
                      setSelectedDeviceIds(selectedDeviceIds.filter((id) => !visibleDeviceIds.includes(id)));
                    }
                  }}
                />
              </th>
            ) : null}
            <th className={`${TABLE_HEAD_CELL_CLASS} min-w-[7rem] whitespace-nowrap`}>
              <button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('deviceId')}>
                Rear device ID{sortIndicator('deviceId')}
              </button>
            </th>
            {isColumnVisible('mode') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('mode')}>Mode{sortIndicator('mode')}</button></th> : null}
            {isColumnVisible('firmware') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('firmware')}>Firmware{sortIndicator('firmware')}</button></th> : null}
            {isColumnVisible('debugLog') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('debugLog')}>Debug log (B){sortIndicator('debugLog')}</button></th> : null}
            {isColumnVisible('raceLog') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('raceLog')}>Race log (B){sortIndicator('raceLog')}</button></th> : null}
            {isColumnVisible('battery') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('battery')}>Battery (V){sortIndicator('battery')}</button></th> : null}
            {isColumnVisible('lastOnline') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('lastOnline')}>Last online{sortIndicator('lastOnline')}</button></th> : null}
            {isColumnVisible('locationUpdated') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('locationUpdated')}>Location updated{sortIndicator('locationUpdated')}</button></th> : null}
            {isColumnVisible('assignedAthleteName') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('assignedAthleteName')}>Athlete name{sortIndicator('assignedAthleteName')}</button></th> : null}
            {isColumnVisible('assignedAthleteRaceNo') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('assignedAthleteRaceNo')}>Athlete #{sortIndicator('assignedAthleteRaceNo')}</button></th> : null}
            {isColumnVisible('racked') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('racked')}>Racked{sortIndicator('racked')}</button></th> : null}
            {isColumnVisible('assignedBy') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('assignedBy')}>Assigned by{sortIndicator('assignedBy')}</button></th> : null}
            {isColumnVisible('assignedAt') ? <th className={TABLE_HEAD_CELL_CLASS}><button type="button" className="font-semibold hover:underline" onClick={() => toggleSort('assignedAt')}>Assigned at{sortIndicator('assignedAt')}</button></th> : null}
            {isAdmin && onRemoveDevice ? (
              <th className={`${TABLE_HEAD_CELL_CLASS} text-right w-16`}>Remove</th>
            ) : null}
          </tr>
        </thead>
        <tbody className={TABLE_BODY_CLASS}>
          {devicesOrderedFiltered.map(({ device, rearDevice, frontDevice, mismatchReasons, primaryDeviceId, rearDeviceId, frontDeviceId }) => {
            const deviceId = String(primaryDeviceId || '').trim();
            const status = statusMap[deviceId];
            const lastSeen = Math.max(lastStatusSeen[deviceId] ?? 0, toDeviceStatusSeenMs(status));
            const cmdStatus = commandStatusMap[deviceId] ?? latestCommandMap[deviceId]?.status;
            const online = lastSeen >= now - ONLINE_TIMEOUT_MS || isCommandActive(cmdStatus);
            const mode = pickMode(online, status);
            const batteryLabel = online ? formatBatteryLabel(status) : 'unknown';
            const batteryClass = online ? battColourClass(status?.batt_mv) : 'bg-gray-100 dark:bg-slate-800 text-gray-500';
            const lastHeardLabel = formatLastHeardLabel(status) || '-';
            const rackedValue = Number(device.racked ?? 0) > 0 ? 1 : 0;
            const retrievedPhase = String(
              retrievedPhaseByDeviceId[deviceId]
              || (rearDeviceId ? retrievedPhaseByDeviceId[rearDeviceId] : '')
              || (frontDeviceId ? retrievedPhaseByDeviceId[frontDeviceId] : '')
              || device.retrievedPhase || '',
            ).trim();
            const isRetrieved = !!retrievedPhase || Number(device.retrieved ?? device.recalled ?? 0) > 0;
            const hasMismatch = mismatchReasons.length > 0;
            const hasStaleData = mismatchReasons.some(isDeviceValidationStaleReason);
            const rowHighlight = resolveAssignedDeviceRowHighlight(
              hasMismatch,
              hasStaleData,
              {
                mismatchReasons,
              },
            );
            const athleteName = String(device.assignedAthleteName ?? '').trim() || '-';
            const athleteRaceNo = String(device.assignedAthleteRaceNo ?? '').trim() || '-';
            const frontAthleteName = String(frontDevice?.assignedAthleteName ?? device.assignedAthleteName ?? '').trim() || '-';
            const frontAthleteRaceNo = String(frontDevice?.assignedAthleteRaceNo ?? device.assignedAthleteRaceNo ?? '').trim() || '-';
            const unused = isUnusedDevice(device);
            const hasFrontOnlyAssignment = !!String(device.assignedAthleteId ?? '').trim() && !rearDeviceId && !!frontDeviceId;

            const frontRetrievedPhase = String(
              (frontDeviceId ? retrievedPhaseByDeviceId[frontDeviceId] : '')
              || frontDevice?.retrievedPhase || '',
            ).trim();
            const frontRetrieved = !!frontRetrievedPhase || Number(frontDevice?.retrieved ?? frontDevice?.recalled ?? 0) > 0;
            const showFrontMiniRow = !!frontDeviceId && !(hideRetrievedRows && frontRetrieved);
            const frontRackedValue = Number(frontDevice?.racked ?? 0) > 0;
            const frontStatus = frontDeviceId ? statusMap[frontDeviceId] : undefined;
            const frontLastSeen = frontDeviceId
              ? Math.max(lastStatusSeen[frontDeviceId] ?? 0, toDeviceStatusSeenMs(frontStatus))
              : 0;
            const rearInRace = rearDeviceId ? isRearRaceModeActive(statusMap, rearDeviceId) : false;
            const frontLinkLabel = String(frontStatus?.front_link_status || '').trim();
            const frontLinkConnected = !frontLinkLabel || frontLinkLabel.toUpperCase() === 'CONNECTED';
            const frontOnline = !rearInRace && frontLinkConnected && frontLastSeen >= now - ONLINE_TIMEOUT_MS;
            const frontMode = pickMode(frontOnline, frontStatus);
            const frontBatteryLabel = frontOnline ? formatBatteryLabel(frontStatus) : 'unknown';
            const frontBatteryClass = frontOnline
              ? battColourClass(frontStatus?.batt_mv)
              : 'bg-gray-100 dark:bg-slate-800 text-gray-500';
            const frontLastHeardLabel = formatLastHeardLabel(frontStatus) || '-';

            return (
              <React.Fragment key={`${deviceId}#${frontDeviceId || '-'}`}>
              <tr
                className={
                  isRetrieved
                    ? ASSIGNED_DEVICE_RETRIEVED_ROW_CLASS
                    : unused
                      ? ASSIGNED_DEVICE_UNUSED_ROW_CLASS
                    : hasFrontOnlyAssignment
                      ? ASSIGNED_DEVICE_FRONT_ONLY_ROW_CLASS
                    : rowHighlight.className
                }
                title={
                  hasFrontOnlyAssignment
                    ? 'Front device assigned without a rear device'
                    : unused
                      ? 'Unused replacement device'
                    : isRetrieved
                      ? `Retrieved${retrievedPhase ? ` during ${retrievedPhase}` : ''}`
                      : rowHighlight.title
                }
              >
                {showDeviceRemovalSelection ? (
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-200 disabled:opacity-50"
                      disabled={isReadOnly}
                      checked={deviceId ? selectedDeviceIds.includes(deviceId) : false}
                      onChange={() => {
                        if (isReadOnly || !deviceId) return;
                        onToggleDeviceSelected(deviceId);
                      }}
                    />
                  </td>
                ) : null}
                <td className="min-w-[6rem] max-w-[6rem] px-2 py-1 align-top">
                  {hasFrontOnlyAssignment ? (
                    <span
                      className="inline-flex items-center rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:border-rose-700/60 dark:bg-rose-950/30 dark:text-rose-200"
                      title="Front device assigned without a rear device"
                    >
                      Missing rear
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={[
                        'overflow-x-auto font-mono text-xs whitespace-nowrap hover:underline cursor-pointer text-left',
                        unused ? 'text-slate-500 dark:text-slate-400' : 'text-gray-900 dark:text-slate-100',
                      ].join(' ')}
                      title={rearDeviceId || deviceId}
                      onClick={() => setRosterDeviceId(rearDeviceId || deviceId)}
                    >
                      {rearDeviceId || deviceId}
                    </button>
                  )}
                </td>
                {isColumnVisible('mode') ? (
                  <td className="px-2 py-1">
                    <ModeBadge mode={mode} />
                  </td>
                ) : null}
                {isColumnVisible('firmware') ? <td className="px-2 py-1 text-gray-700 dark:text-slate-300">{String(status?.fw || '-')}</td> : null}
                {isColumnVisible('debugLog') ? (
                  <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap">
                    {typeof status?.debug_log_size === 'number' ? status.debug_log_size : '—'}
                  </td>
                ) : null}
                {isColumnVisible('raceLog') ? (
                  <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap">
                    {typeof status?.race_log_size === 'number' ? status.race_log_size : '—'}
                  </td>
                ) : null}
                {isColumnVisible('battery') ? (
                  <td className="px-2 py-1">
                    <div className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-semibold ${batteryClass}`}>
                      {batteryLabel}
                    </div>
                  </td>
                ) : null}
                {isColumnVisible('lastOnline') ? <td className="px-2 py-1 whitespace-nowrap text-gray-700 dark:text-slate-300">{lastHeardLabel}</td> : null}
                {isColumnVisible('locationUpdated') ? (
                  <td className="px-2 py-1 whitespace-nowrap text-gray-700 dark:text-slate-300">
                    {formatTime(status?.last_location_updated_at) || '—'}
                  </td>
                ) : null}
                {isColumnVisible('assignedAthleteName') ? (
                  <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap max-w-[160px] truncate" title={athleteName}>
                    {isRetrieved ? renderRetrievedPhaseBadge(retrievedPhase) : unused ? renderUnusedBadge() : athleteName}
                  </td>
                ) : null}
                {isColumnVisible('assignedAthleteRaceNo') ? (
                  <td className="px-2 py-1 font-mono text-xs text-gray-700 dark:text-slate-300 whitespace-nowrap">
                    {isRetrieved ? '—' : athleteRaceNo}
                  </td>
                ) : null}
                {isColumnVisible('racked') ? (
                  <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap">
                    {unused ? '-' : rackedValue ? '✓' : '✕'}
                  </td>
                ) : null}
                {isColumnVisible('assignedBy') ? (
                  <td
                    className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap max-w-[180px] truncate"
                    title={formatUserIdentityDisplay(device.assignedBy, globalUserIdToDisplayName)}
                  >
                    {formatUserIdentityDisplay(device.assignedBy, globalUserIdToDisplayName)}
                  </td>
                ) : null}
                {isColumnVisible('assignedAt') ? <td className="px-2 py-1 whitespace-nowrap text-gray-700 dark:text-slate-300">{formatTs(device.assignedAt)}</td> : null}
                {isAdmin && onRemoveDevice ? (
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      disabled={isReadOnly}
                      className="inline-flex items-center justify-center h-7 w-7 rounded border border-red-300 bg-red-50 dark:border-red-700/60 dark:bg-red-950/40 text-[11px] font-semibold text-red-800 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-50"
                      title={isReadOnly ? lockedHint : 'Remove device'}
                      aria-label="Remove device"
                      onClick={() => {
                        if (isReadOnly || !deviceId) return;
                        onRemoveDevice(eventId, deviceId);
                      }}
                    >
                      -
                    </button>
                  </td>
                ) : null}
              </tr>
              {showFrontMiniRow ? (
                <tr
                  className={
                    frontRetrieved
                      ? `${ASSIGNED_DEVICE_ROW_STATE_BASE} !border-l-amber-500 bg-amber-50/70 dark:!border-l-amber-400 dark:bg-amber-950/20`
                      : rowHighlight.className
                  }
                  title={
                    frontRetrieved
                      ? `Retrieved${frontRetrievedPhase ? ` during ${frontRetrievedPhase}` : ''}`
                      : frontDeviceId
                        ? `Paired front: ${frontDeviceId}${frontLinkLabel ? ` · BLE ${frontLinkLabel}` : ''}`
                        : undefined
                  }
                >
                  {showDeviceRemovalSelection ? <td className="px-2 py-1" /> : null}
                  <td className="min-w-[6rem] max-w-[6rem] px-2 py-1 pl-4 align-top">
                    <span
                      className="text-[10px] text-blue-700 dark:text-blue-300 font-mono whitespace-nowrap"
                      title={frontDeviceId}
                    >
                      ↳ Paired front: {frontDeviceId}
                    </span>
                  </td>
                  {isColumnVisible('mode') ? (
                    <td className="px-2 py-1">
                      <ModeBadge mode={frontMode} />
                    </td>
                  ) : null}
                  {isColumnVisible('firmware') ? (
                    <td className="px-2 py-1 text-gray-700 dark:text-slate-300">{String(frontStatus?.fw || '-')}</td>
                  ) : null}
                  {isColumnVisible('debugLog') ? (
                    <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap">
                      {typeof frontStatus?.debug_log_size === 'number' ? frontStatus.debug_log_size : '—'}
                    </td>
                  ) : null}
                  {isColumnVisible('raceLog') ? (
                    <td className="px-2 py-1 text-gray-700 dark:text-slate-300 whitespace-nowrap">
                      {typeof frontStatus?.race_log_size === 'number' ? frontStatus.race_log_size : '—'}
                    </td>
                  ) : null}
                  {isColumnVisible('battery') ? (
                    <td className="px-2 py-1">
                      <div className={`inline-flex items-center justify-center px-2 py-1 rounded-md text-xs font-semibold ${frontBatteryClass}`}>
                        {frontBatteryLabel}
                      </div>
                    </td>
                  ) : null}
                  {isColumnVisible('lastOnline') ? (
                    <td className="px-2 py-1 whitespace-nowrap text-gray-700 dark:text-slate-300">{frontLastHeardLabel}</td>
                  ) : null}
                  {isColumnVisible('locationUpdated') ? <td className="px-2 py-1" /> : null}
                  {isColumnVisible('assignedAthleteName') ? (
                    <td className="px-2 py-1 text-[10px] text-gray-700 dark:text-slate-300 whitespace-nowrap max-w-[160px] truncate" title={frontAthleteName}>
                      {frontRetrieved ? renderRetrievedPhaseBadge(frontRetrievedPhase, true) : frontAthleteName}
                    </td>
                  ) : null}
                  {isColumnVisible('assignedAthleteRaceNo') ? (
                    <td className="px-2 py-1 font-mono text-[10px] text-gray-700 dark:text-slate-300 whitespace-nowrap">
                      {frontRetrieved ? '—' : frontAthleteRaceNo}
                    </td>
                  ) : null}
                  {isColumnVisible('racked') ? (
                    <td className="px-2 py-1 text-[10px] text-gray-700 dark:text-slate-300 whitespace-nowrap">
                      {frontRackedValue ? '✓' : '✕'}
                    </td>
                  ) : null}
                  {isColumnVisible('assignedBy') ? <td className="px-2 py-1" /> : null}
                  {isColumnVisible('assignedAt') ? <td className="px-2 py-1" /> : null}
                  {isAdmin && onRemoveDevice ? <td className="px-2 py-1" /> : null}
                </tr>
              ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }

  if (devices.length === 0) {
    return <div className="p-4 text-sm text-gray-600 dark:text-slate-400">No devices assigned.</div>;
  }

  return (
    <div>
      {raceConfigurationLoading ? (
        <div className="px-4 py-1.5 text-xs text-gray-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-700">Loading race configuration…</div>
      ) : null}
      {raceConfigurationNotice}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2">
        <DeviceIdTableFilterBar
          value={deviceIdFilter}
          onChange={setDeviceIdFilter}
          enableScanner={!devicesViewExpanded}
          className="min-w-0 flex-1"
          inputClassName="h-9 min-w-0 flex-1 border-0 bg-transparent px-2.5 text-[11px] shadow-none outline-none ring-0 focus:ring-0"
        />
        <div className="flex shrink-0 items-center gap-2">
          {showRetrievalUi ? (
            <label className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 dark:text-slate-200">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-200"
                checked={hideRetrievedRows}
                onChange={(e) => setHideRetrievedRows(e.target.checked)}
              />
              Hide retrieved
            </label>
          ) : null}
          <button
            type="button"
            onClick={async () => {
              if (!onRefresh || refreshingRows) return;
              try {
                setRefreshingRows(true);
                await Promise.resolve(onRefresh());
              } finally {
                setRefreshingRows(false);
              }
            }}
            disabled={!onRefresh || refreshingRows}
            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-50"
            title="Refresh devices"
            aria-label="Refresh devices"
          >
            <span className={refreshingRows ? 'animate-spin' : ''}>↻</span>
          </button>
          <button
            ref={columnsMenuButtonRef}
            type="button"
            onClick={() => setColumnsMenuOpen((prev) => !prev)}
            className="inline-flex items-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
            aria-haspopup="dialog"
            aria-expanded={columnsMenuOpen}
          >
            Columns
          </button>
          <button
            type="button"
            onClick={() => setDevicesViewExpanded(true)}
            className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 dark:border-blue-700/60 dark:bg-blue-950/40 px-2.5 py-1.5 text-[11px] font-semibold text-blue-900 hover:bg-blue-100"
          >
            Expand view
          </button>
        </div>
      </div>
      {columnsMenuOpen && typeof document !== 'undefined'
        ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[5200] bg-slate-900/25"
              role="presentation"
              onClick={() => setColumnsMenuOpen(false)}
            />
            <div
              className="fixed z-[5210] flex flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-left shadow-2xl ring-1 ring-black/5"
              style={{
                top: columnsMenuCoords.top,
                left: columnsMenuCoords.left,
                width: columnsMenuCoords.width,
                maxHeight: columnsMenuCoords.maxHeight,
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Choose visible columns"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2">
                <div className="text-xs font-semibold text-gray-800 dark:text-slate-200">Visible columns</div>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-slate-400">
                  Device ID is always shown.
                </p>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-1.5 py-2">
                {ASSIGNED_DEVICES_COLUMNS.map((column) => (
                  <label
                    key={column.key}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-200"
                      checked={isColumnVisible(column.key)}
                      onChange={() => toggleColumn(column.key)}
                    />
                    <span>{column.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </>,
          document.body,
        )
        : null}
      {devicesViewExpanded ? (
        <div className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-2 text-xs text-gray-600 dark:text-slate-400">
          Expanded view is open — use the overlay for a larger search field and table. Close the overlay or press
          Escape.
        </div>
      ) : (
        <div className="max-h-56 overflow-auto border-b border-gray-100 dark:border-slate-700">{renderAssignedDevicesDataTable()}</div>
      )}
      {devicesViewExpanded ? (
        <PortaledLayer>
          <div
            data-race-stacked-overlay=""
            className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/50 p-3 dark:bg-black/70 sm:p-6"
            onClick={(e) => {
              if (e.target === e.currentTarget) setDevicesViewExpanded(false);
            }}
            role="presentation"
          >
            <div
              className="flex max-h-[min(92vh,900px)] w-full max-w-[min(1600px,100vw-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              role="dialog"
              aria-modal="true"
              aria-labelledby="event-devices-expand-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                <h2 id="event-devices-expand-title" className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                  Assigned devices
                </h2>
                <button
                  type="button"
                  onClick={() => setDevicesViewExpanded(false)}
                  className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
              {raceConfigurationNotice}
              <div className="flex min-h-0 flex-1 flex-col gap-3 border-b border-gray-100 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                <DeviceIdTableFilterBar
                  value={deviceIdFilter}
                  onChange={setDeviceIdFilter}
                  enableScanner
                  className="w-full"
                  inputClassName="min-h-[3rem] w-full flex-1 border-0 bg-transparent px-4 text-sm shadow-none outline-none ring-0 focus:ring-0"
                />
                <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                  {renderAssignedDevicesDataTable()}
                </div>
              </div>
            </div>
          </div>
        </PortaledLayer>
      ) : null}
      <DeviceRosterOverlay
        deviceId={rosterDeviceId}
        operatorScopedEventId={eventId}
        onClose={() => setRosterDeviceId(null)}
      />
    </div>
  );
}
